/*
 * Free Gift Auto-Add
 * Adds 1x SKU 6530 (Creatin) to the cart automatically when cart has items.
 * The matching automatic discount "GRATIS CREATIN" reduces price to 0,00 €.
 * Customer can remove the gift; we then stop re-adding it for the session.
 *
 * Robustness layers (against Fast Bundle and other apps that rewrite the cart DOM):
 *  1. Dawn pubsub subscription (cartUpdate)
 *  2. window.fetch interceptor — detects cart mutations from any source
 *  3. MutationObserver on cart-drawer — re-renders if external code wiped our gift line
 *  4. Delayed re-checks (1.5s + 3s after load) — catches late-rendering apps
 */
(function () {
  'use strict';

  var ext = (typeof window !== 'undefined' && window.__giftConfig) ? window.__giftConfig : {};

  var CONFIG = {
    variantId: typeof ext.variantId === 'number' ? ext.variantId : null,
    triggerType: ext.triggerType === 'min_amount' ? 'min_amount' : 'always',
    minAmountCents: typeof ext.minAmountCents === 'number' ? ext.minAmountCents : 0,
    validFrom: typeof ext.validFrom === 'string' ? ext.validFrom : '',
    validUntil: typeof ext.validUntil === 'string' ? ext.validUntil : '',
    propertyKey: '_free_gift',
    removedFlagKey: 'alphaFreeGiftRemoved',
    discountFailedKey: 'alphaFreeGiftDiscountFailed',
    ownRequestHeader: 'X-Gift-Auto-Add'
  };

  // No variant configured → feature off
  if (!CONFIG.variantId) return;

  // Returns today as 'YYYY-MM-DD' using the customer's local date
  function todayLocalISO() {
    var d = new Date();
    var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function isWithinValidRange() {
    var today = todayLocalISO();
    if (CONFIG.validFrom && today < CONFIG.validFrom) return false;
    if (CONFIG.validUntil && today > CONFIG.validUntil) return false;
    return true;
  }

  // Out of validity window at script load → don't even install observers
  if (!isWithinValidRange()) return;

  var isProcessing = false;
  var lastKnownGiftPresent = null;
  var ownRenderInProgress = false;

  function isGiftLine(item) {
    return item && item.properties && item.properties[CONFIG.propertyKey] === 'true';
  }

  function cartHasGift(cart) {
    return cart && Array.isArray(cart.items) && cart.items.some(isGiftLine);
  }

  function cartHasNonGiftItems(cart) {
    return cart && Array.isArray(cart.items) && cart.items.some(function (item) {
      return !isGiftLine(item);
    });
  }

  function nonGiftSubtotalCents(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var sum = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (isGiftLine(item)) continue;
      var unit = (typeof item.original_price === 'number') ? item.original_price : (item.price || 0);
      sum += unit * (item.quantity || 0);
    }
    return sum;
  }

  function shouldHaveGift(cart) {
    if (!isWithinValidRange()) return false;
    if (!cartHasNonGiftItems(cart)) return false;
    if (CONFIG.triggerType === 'min_amount') {
      return nonGiftSubtotalCents(cart) >= CONFIG.minAmountCents;
    }
    return true;
  }

  // Returns true if the gift is in the cart but the eligibility conditions
  // are no longer met (no other items, or below min_amount threshold).
  function shouldRemoveOrphanedGift(cart) {
    if (!cartHasGift(cart)) return false;
    if (!cartHasNonGiftItems(cart)) return true;
    if (CONFIG.triggerType === 'min_amount'
        && nonGiftSubtotalCents(cart) < CONFIG.minAmountCents) {
      return true;
    }
    return false;
  }

  function isRemovedBySession() {
    try { return sessionStorage.getItem(CONFIG.removedFlagKey) === '1'; }
    catch (e) { return false; }
  }

  function setRemovedBySession(v) {
    try {
      if (v) sessionStorage.setItem(CONFIG.removedFlagKey, '1');
      else sessionStorage.removeItem(CONFIG.removedFlagKey);
    } catch (e) {}
  }

  function isDiscountFailed() {
    try { return sessionStorage.getItem(CONFIG.discountFailedKey) === '1'; }
    catch (e) { return false; }
  }

  function setDiscountFailed(v) {
    try {
      if (v) sessionStorage.setItem(CONFIG.discountFailedKey, '1');
      else sessionStorage.removeItem(CONFIG.discountFailedKey);
    } catch (e) {}
  }

  // True if the Shopify "Buy X get Y" discount actually reduced the gift line
  // to zero. Used to detect "auto-add went through, but the discount didn't
  // match (e.g. qualifying collection was removed in admin)" — in which case
  // we silently undo the add so the customer never pays for the gift.
  function isGiftDiscountedInCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    var giftItem = cart.items.filter(isGiftLine)[0];
    if (!giftItem) return false;
    if (giftItem.original_line_price === 0) return true;
    return giftItem.total_discount >= giftItem.original_line_price;
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function getSectionsList() {
    var sections = ['cart-drawer', 'cart-icon-bubble'];
    if (document.getElementById('main-cart-items')) sections.push('cart-items');
    if (document.getElementById('main-cart-footer')) sections.push('cart-footer');
    return sections;
  }

  function addGiftToCart() {
    var properties = {};
    properties[CONFIG.propertyKey] = 'true';
    var headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
    headers[CONFIG.ownRequestHeader] = '1';
    var body = JSON.stringify({
      items: [{ id: CONFIG.variantId, quantity: 1, properties: properties }],
      sections: getSectionsList().join(','),
      sections_url: window.location.pathname
    });
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers,
      body: body
    }).then(function (res) {
      if (!res.ok) throw new Error('add failed (' + res.status + ')');
      return res.json();
    });
  }

  function removeGiftFromCart(giftKey) {
    var headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
    headers[CONFIG.ownRequestHeader] = '1';
    var body = JSON.stringify({
      id: giftKey,
      quantity: 0,
      sections: getSectionsList().join(','),
      sections_url: window.location.pathname
    });
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers,
      body: body
    }).then(function (res) {
      if (!res.ok) throw new Error('change failed (' + res.status + ')');
      return res.json();
    });
  }

  function removeOrphanedGift(cart) {
    if (isProcessing) return Promise.resolve();
    var giftItem = cart.items.filter(isGiftLine)[0];
    if (!giftItem) return Promise.resolve();

    isProcessing = true;
    return removeGiftFromCart(giftItem.key)
      .then(function (result) {
        lastKnownGiftPresent = false;
        if (result.item_count === 0) setRemovedBySession(false);
        refreshSectionsFromResponse(result);
        publishCartUpdate(result);
      })
      .catch(function (e) { console.warn('[gift-auto-add] orphan remove error:', e); })
      .finally(function () { isProcessing = false; });
  }

  function fetchSectionsOnly() {
    var path = (window.location && window.location.pathname) || '/';
    var url = path + '?sections=' + getSectionsList().join(',');
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    }).then(function (data) {
      return { sections: data };
    });
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function refreshCartDrawer(html) {
    if (!html) return;
    var drawerInner = document.querySelector('cart-drawer .drawer__inner');
    var drawerWrapper = document.querySelector('cart-drawer');
    var source = parseHTML(html).querySelector('.drawer__inner');
    if (!drawerInner || !source) return;
    drawerInner.innerHTML = source.innerHTML;
    if (drawerWrapper) drawerWrapper.classList.remove('is-empty');
    var drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.remove('is-empty');
  }

  function refreshCartIconBubble(html) {
    if (!html) return;
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;
    var doc = parseHTML(html);
    var source = doc.getElementById('cart-icon-bubble') || doc.querySelector('.shopify-section') || doc.body;
    if (source) target.innerHTML = source.innerHTML;
  }

  function refreshCartItems(html) {
    if (!html) return;
    var target = document.getElementById('main-cart-items');
    if (!target) return;
    var source = parseHTML(html).getElementById('main-cart-items');
    if (source) target.innerHTML = source.innerHTML;
    var wrapper = document.querySelector('cart-items');
    if (wrapper) wrapper.classList.remove('is-empty');
  }

  function refreshCartFooter(html) {
    if (!html) return;
    var target = document.getElementById('main-cart-footer');
    if (!target) return;
    var source = parseHTML(html).getElementById('main-cart-footer');
    if (source) target.innerHTML = source.innerHTML;
    target.classList.remove('is-empty');
  }

  function refreshSectionsFromResponse(data) {
    if (!data || !data.sections) return;
    ownRenderInProgress = true;
    try {
      refreshCartDrawer(data.sections['cart-drawer']);
      refreshCartIconBubble(data.sections['cart-icon-bubble']);
      refreshCartItems(data.sections['cart-items']);
      refreshCartFooter(data.sections['cart-footer']);
    } finally {
      // Release the flag quickly — our innerHTML mutations are synchronous, so a
      // long window only masks external stale-writes that we DO want to catch.
      setTimeout(function () { ownRenderInProgress = false; }, 100);
    }
  }

  function publishCartUpdate(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'gift-auto-add',
        cartData: cartData,
        variantId: CONFIG.variantId
      });
    }
  }

  function checkAndAddGift() {
    if (isProcessing) return Promise.resolve();
    if (isRemovedBySession()) return Promise.resolve();
    if (isDiscountFailed()) return Promise.resolve();

    isProcessing = true;
    return getCart()
      .then(function (cart) {
        if (cart.item_count === 0) {
          setRemovedBySession(false);
          setDiscountFailed(false);
          lastKnownGiftPresent = false;
          return;
        }
        if (cartHasGift(cart)) {
          // Gift already present — but does the Shopify discount actually apply?
          if (!isGiftDiscountedInCart(cart)) {
            var orphan = cart.items.filter(isGiftLine)[0];
            setDiscountFailed(true);
            return removeGiftFromCart(orphan.key).then(function (result) {
              lastKnownGiftPresent = false;
              refreshSectionsFromResponse(result);
              publishCartUpdate(result);
            });
          }
          lastKnownGiftPresent = true;
          return;
        }
        if (!shouldHaveGift(cart)) return;

        return addGiftToCart().then(function (addResult) {
          // /cart/add.js returns the added line item(s) with computed
          // discount fields (total_discount, original_line_price, key) —
          // we don't need a separate /cart.js round-trip to verify.
          var addedGift = null;
          if (addResult && Array.isArray(addResult.items)) {
            addedGift = addResult.items.filter(function (i) {
              return i && i.properties && i.properties[CONFIG.propertyKey] === 'true';
            })[0];
          }

          var isDiscounted = !!addedGift
            && (addedGift.original_line_price === 0
                || addedGift.total_discount >= addedGift.original_line_price);

          if (!isDiscounted) {
            setDiscountFailed(true);
            console.warn('[gift-auto-add] Buy-X-get-Y did not apply — removing gift.', {
              total_discount: addedGift && addedGift.total_discount,
              original_line_price: addedGift && addedGift.original_line_price
            });
            if (!addedGift || !addedGift.key) {
              // Defensive: couldn't read the gift from add response; fall
              // back to /cart.js to find the line key.
              return getCart().then(function (cart) {
                var item = cart.items.filter(isGiftLine)[0];
                if (!item) {
                  lastKnownGiftPresent = false;
                  return;
                }
                return removeGiftFromCart(item.key).then(function (removeResult) {
                  lastKnownGiftPresent = false;
                  refreshSectionsFromResponse(removeResult);
                  publishCartUpdate(removeResult);
                });
              });
            }
            return removeGiftFromCart(addedGift.key).then(function (removeResult) {
              lastKnownGiftPresent = false;
              refreshSectionsFromResponse(removeResult);
              publishCartUpdate(removeResult);
            });
          }

          lastKnownGiftPresent = true;
          refreshSectionsFromResponse(addResult);
          publishCartUpdate(addResult);
          // Defensive: Dawn's product-form publishes cartUpdate BEFORE our add
          // fires, which causes cart-drawer-items.onCartUpdate to start a stale
          // /cart?section_id=cart-drawer fetch. If that fetch's response arrives
          // after our render, it overwrites our gift line. Re-verify several
          // times to catch and undo that wipe.
          setTimeout(verifyGiftVisible, 800);
          setTimeout(verifyGiftVisible, 1600);
          setTimeout(verifyGiftVisible, 2800);
        });
      })
      .catch(function (e) { console.warn('[gift-auto-add] error:', e); })
      .finally(function () { isProcessing = false; });
  }

  // After an external app mutates the cart, make sure DOM still reflects gift
  function verifyGiftVisible() {
    if (isProcessing) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var giftLineInDrawer = drawer.querySelector('.cart-item--free-gift');
    var cartPage = document.getElementById('main-cart-items');
    var giftLineInCart = cartPage ? cartPage.querySelector('.cart-item--free-gift') : null;

    getCart().then(function (cart) {
      var giftInCart = cartHasGift(cart);

      // Eligibility no longer holds → remove the gift
      if (shouldRemoveOrphanedGift(cart)) {
        return removeOrphanedGift(cart);
      }

      // Gift is in cart but Buy-X-get-Y didn't zero it → silent remove + flag
      if (giftInCart && !isGiftDiscountedInCart(cart)) {
        return removeGiftDueToFailedDiscount(cart);
      }

      // Cart has gift but DOM doesn't — re-render sections
      if (giftInCart && !giftLineInDrawer && !drawer.classList.contains('is-empty')) {
        return fetchSectionsOnly().then(refreshSectionsFromResponse);
      }
      if (giftInCart && cartPage && !giftLineInCart) {
        return fetchSectionsOnly().then(refreshSectionsFromResponse);
      }

      // Cart doesn't have gift but should — add it
      if (!giftInCart && shouldHaveGift(cart) && !isRemovedBySession() && !isDiscountFailed()) {
        return checkAndAddGift();
      }
    }).catch(function (e) { console.warn('[gift-auto-add] verify error:', e); });
  }

  function removeGiftDueToFailedDiscount(cart) {
    if (isProcessing) return Promise.resolve();
    var giftItem = cart.items.filter(isGiftLine)[0];
    if (!giftItem) {
      setDiscountFailed(true);
      lastKnownGiftPresent = false;
      return Promise.resolve();
    }

    isProcessing = true;
    setDiscountFailed(true);
    return removeGiftFromCart(giftItem.key)
      .then(function (result) {
        lastKnownGiftPresent = false;
        refreshSectionsFromResponse(result);
        publishCartUpdate(result);
      })
      .catch(function (e) { console.warn('[gift-auto-add] discount-fail remove error:', e); })
      .finally(function () { isProcessing = false; });
  }

  function handleCartUpdateEvent(event) {
    if (!event || event.source === 'gift-auto-add') return;
    // External cart change — give the discount another shot (admin could have
    // just enabled a matching collection, or user added a qualifying item).
    setDiscountFailed(false);
    var cart = event.cartData;
    if (!cart) { setTimeout(checkAndAddGift, 100); return; }

    if (cart.item_count === 0) {
      setRemovedBySession(false);
      setDiscountFailed(false);
      lastKnownGiftPresent = false;
      return;
    }

    // Auto-remove the gift if the eligibility no longer holds (no other items,
    // or subtotal dropped below threshold). Do NOT mark as user-removed.
    if (shouldRemoveOrphanedGift(cart)) {
      removeOrphanedGift(cart);
      return;
    }

    var currentlyHasGift = cartHasGift(cart);

    if (lastKnownGiftPresent === true && !currentlyHasGift) {
      setRemovedBySession(true);
      lastKnownGiftPresent = false;
      return;
    }

    lastKnownGiftPresent = currentlyHasGift;

    if (!currentlyHasGift && !isRemovedBySession()) {
      setTimeout(checkAndAddGift, 150);
    }
  }

  // Intercept fetch to catch cart mutations from third-party apps (Fast Bundle etc.)
  function installFetchInterceptor() {
    if (window.__giftFetchPatched) return;
    window.__giftFetchPatched = true;

    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = '';
      try {
        if (typeof args[0] === 'string') url = args[0];
        else if (args[0] && args[0].url) url = args[0].url;
      } catch (e) {}

      var isOurs = false;
      try {
        var opts = args[1];
        if (opts && opts.headers) {
          if (opts.headers[CONFIG.ownRequestHeader]
              || (typeof opts.headers.get === 'function' && opts.headers.get(CONFIG.ownRequestHeader))) {
            isOurs = true;
          }
        }
      } catch (e) {}

      return origFetch.apply(this, args).then(function (response) {
        try {
          if (!isOurs && /\/cart\/(add|change|update|clear)/.test(url)) {
            // Some external cart mutation just happened — verify + re-sync.
            // Give the discount another shot in case eligibility changed.
            setDiscountFailed(false);
            setTimeout(verifyGiftVisible, 250);
            setTimeout(verifyGiftVisible, 1000);
          }
        } catch (e) {}
        return response;
      });
    };
  }

  // Watch the cart-drawer for external DOM rewrites (Fast Bundle, etc.)
  function installDrawerObserver() {
    var attempts = 0;
    function tryAttach() {
      var target = document.querySelector('cart-drawer .drawer__inner');
      if (!target) {
        if (attempts++ < 20) setTimeout(tryAttach, 300);
        return;
      }
      var observer = new MutationObserver(function () {
        if (ownRenderInProgress) return;
        // Debounce: wait briefly for external render to settle, then verify
        clearTimeout(window.__giftDrawerVerifyTimer);
        window.__giftDrawerVerifyTimer = setTimeout(verifyGiftVisible, 300);
      });
      observer.observe(target, { childList: true, subtree: false });
    }
    tryAttach();
  }

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdateEvent);
    }

    installFetchInterceptor();
    installDrawerObserver();

    // Initial check immediately
    checkAndAddGift();

    // Late re-checks to catch apps that render after DOMContentLoaded
    setTimeout(verifyGiftVisible, 1500);
    setTimeout(verifyGiftVisible, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
